import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { DollarSign, TrendingDown, TrendingUp, Percent, Download } from 'lucide-react';
import { GET_REPORTE_ESTADO_RESULTADOS } from '../../graphql/reportes';
import FiltrosReporte from './FiltrosReporte';
import Loading from '../shared/Loading';
import Button from '../shared/Button';
import './ReporteEstadoResultados.css';

function ReporteEstadoResultados() {
  const [datos, setDatos] = useState(null);
  const [getReporte, { loading, error }] = useLazyQuery(GET_REPORTE_ESTADO_RESULTADOS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => setDatos(data.reporteEstadoResultados),
    onError: (err) => console.error('Error al obtener Estado de Resultados:', err)
  });

  const handleFiltrar = ({ fechaDesde, fechaHasta }) => {
    getReporte({ variables: { fechaDesde, fechaHasta } });
  };

  const formatearMoneda = (valor) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);

  const formatearPorcentaje = (valor) => `${parseFloat(valor || 0).toFixed(2)}%`;

  return (
    <div className="reporte-estado-resultados">
      <FiltrosReporte onFiltrar={handleFiltrar} loading={loading} />

      {loading && <Loading />}
      {error && <div className="error-message">Error: {error.message}</div>}

      {datos && (
        <>
          {/* Cards resumen */}
          <div className="reporte-cards">
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--success">
                <DollarSign size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Total Ingresos</div>
                <div className="reporte-card__value">{formatearMoneda(datos.total_ingresos)}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--danger">
                <TrendingDown size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Total Egresos</div>
                <div className="reporte-card__value">{formatearMoneda(datos.total_egresos)}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className={`reporte-card__icon reporte-card__icon--${datos.utilidad_neta >= 0 ? 'success' : 'danger'}`}>
                <TrendingUp size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Utilidad Neta</div>
                <div className={`reporte-card__value ${datos.utilidad_neta < 0 ? 'text-danger' : 'text-success'}`}>
                  {formatearMoneda(datos.utilidad_neta)}
                </div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--info">
                <Percent size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Margen Neto</div>
                <div className="reporte-card__value">{formatearPorcentaje(datos.margen_neto)}</div>
              </div>
            </div>
          </div>

          {/* Desglose Ingresos */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Desglose de Ingresos</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>% del Total</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.desglose_ingresos.map(item => (
                    <tr key={item.concepto}>
                      <td><strong>{item.concepto}</strong></td>
                      <td>{formatearMoneda(item.monto)}</td>
                      <td>{formatearPorcentaje(item.porcentaje)}</td>
                    </tr>
                  ))}
                  <tr className="fila-total">
                    <td><strong>TOTAL INGRESOS</strong></td>
                    <td><strong>{formatearMoneda(datos.total_ingresos)}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Desglose Egresos */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Desglose de Egresos</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>% del Total</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.desglose_egresos.map(item => (
                    <tr key={item.concepto}>
                      <td>{item.concepto}</td>
                      <td>{formatearMoneda(item.monto)}</td>
                      <td>{formatearPorcentaje(item.porcentaje)}</td>
                    </tr>
                  ))}
                  <tr className="fila-total">
                    <td><strong>TOTAL EGRESOS</strong></td>
                    <td><strong>{formatearMoneda(datos.total_egresos)}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Resultado diario */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Resultado Diario</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Ingresos</th>
                    <th>Egresos</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.resultado_por_dia.map(dia => (
                    <tr key={dia.fecha}>
                      <td>{new Date(dia.fecha).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })}</td>
                      <td className="text-success">{formatearMoneda(dia.ingresos)}</td>
                      <td className="text-danger">{formatearMoneda(dia.egresos)}</td>
                      <td className={dia.resultado >= 0 ? 'text-success' : 'text-danger'}>
                        <strong>{formatearMoneda(dia.resultado)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!datos && !loading && (
        <div className="reporte-placeholder">
          <DollarSign size={64} />
          <p>Selecciona un rango de fechas para generar el Estado de Resultados</p>
        </div>
      )}
    </div>
  );
}

export default ReporteEstadoResultados;
